import { FC } from 'react';

import { toUsFormat } from 'utils/utils';

import type { LineItem, PunchupLineItemListProps } from './types';
import styles from './PunchupLineItemList.module.css';

/**
 * PunchupLineItemList
 *
 * Renders a list of `(N× label …… $price)` rows — the count/name/price breakdown
 * shared by the Send Tickets confirmation box and the Comped / Transactions table
 * cells. Pure presentation: callers shape the items (group, count, and pre-apply
 * the line total) before passing them in.
 *
 * Rows with `count <= 0` are skipped, and the whole list renders `null` when no
 * rows remain — so parents don't need a length guard (matches
 * `PunchupSeatsCategoryBreakdown`). The price column is omitted per-row when
 * `price` is null/undefined, so a price-less context (comps) reads as just `N× label`.
 *
 * Rows are sorted by `sortKey` (or the string `label`) so the same set of items always
 * renders in the same order, regardless of the order the caller grouped them in.
 */
const sortKeyOf = (item: LineItem): string =>
	(item.sortKey ?? (typeof item.label === 'string' ? item.label : '')).toLowerCase();

export const PunchupLineItemList: FC<PunchupLineItemListProps> = ({ items, className }) => {
	const rows = items.filter((item) => item.count > 0).sort((a, b) => sortKeyOf(a).localeCompare(sortKeyOf(b)));
	if (rows.length === 0) return null;

	return (
		<div className={[styles.list, className].filter(Boolean).join(' ')} data-testid="line-item-list">
			{rows.map((item, i) => (
				// Prefer sortKey (stable across the .sort() reorder) so rows don't churn the DOM when
				// the order changes; fall back to the index when sortKey is absent (label may be a node
				// — a ticket-type chip — so it can't be interpolated into a string key).
				<p className={styles.lineItem} key={item.sortKey ?? i}>
					<span className={styles.lineItemLeft}>
						<span className={styles.qty}>{item.count}×</span>
						<span>{item.label}</span>
					</span>
					{item.price != null && <span className={styles.lineItemPrice}>{toUsFormat(item.price)}</span>}
				</p>
			))}
		</div>
	);
};
